import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
    Container, Box, Typography, Paper, Button, IconButton, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, useMediaQuery, useTheme
} from '@mui/material';
import {
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
    LocalOffer as CouponIcon, ContentCopy as CopyIcon,
    Percent as PercentIcon, CurrencyRupee as RupeeIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from "../../services/api";
import AddCouponDialog from './AddCouponDialog';
import EditCouponDialog from './EditCouponDialog';

const dialogPaperSx = {
    background: 'linear-gradient(135deg, #1a1510 0%, #0f0d0a 100%)',
    border: '1px solid rgba(188, 150, 94, 0.82)',
    borderRadius: 3,
    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8)',
};

const STAT_CARDS = [
    { key: 'total', label: 'Total', color: '#bc965e' },
    { key: 'active', label: 'Active', color: '#4caf50' },
    { key: 'inactive', label: 'Inactive', color: '#9e9e9e' },
    { key: 'expired', label: 'Expired', color: '#f44336' },
];

const formatDiscount = (coupon) =>
    coupon.DiscountType === 'percentage'
        ? `${coupon.DiscountValue}`
        : `₹${parseFloat(coupon.DiscountValue).toLocaleString()}`;

// ── Square filled action button (edit / delete) ─────────────────────────────
const SquareActionButton = memo(({ icon, color, bg, border, onClick }) => (
    <IconButton
        size="small"
        onClick={onClick}
        sx={{
            width: 32, height: 32, borderRadius: 1.5,
            background: bg,
            border: `1px solid ${border}`,
            color,
            '&:hover': { background: border, color: '#fff' },
        }}
    >
        {icon}
    </IconButton>
));

// ── Single coupon row, memoized so unrelated state changes (e.g. opening a
// dialog) don't force every row in the table to re-render ─────────────────
const CouponRow = memo(({ coupon, expired, onCopy, onToggleActive, onEdit, onDelete }) => (
    <TableRow sx={{
        '& .MuiTableCell-root': {
            borderColor: 'rgba(188,150,94,0.08)',
            color: 'white',
            whiteSpace: 'nowrap',
        },
        '&:hover': { backgroundColor: 'rgba(188,150,94,0.05)' },
        opacity: expired ? 0.6 : 1,
    }}>
        {/* Code */}
        <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Typography sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: 'white',
                    fontSize: '0.85rem',
                }}>
                    {coupon.CouponCode}
                </Typography>
                <IconButton size="small" onClick={() => onCopy(coupon.CouponCode)}
                    sx={{
                        width: 28, height: 28, borderRadius: 1.5,
                        color: 'rgba(255,255,255,0.4)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        '&:hover': { color: '#bc965e', borderColor: 'rgba(188,150,94,0.4)' },
                    }}>
                    <CopyIcon sx={{ fontSize: 15 }} />
                </IconButton>
            </Box>
        </TableCell>

        {/* Discount */}
        <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {coupon.DiscountType === 'percentage'
                    ? <PercentIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                    : <RupeeIcon sx={{ fontSize: 14, color: '#2196f3' }} />
                }
                <Typography sx={{ fontWeight: 700, color: coupon.DiscountType === 'percentage' ? '#4caf50' : '#2196f3' }}>
                    {formatDiscount(coupon)}
                </Typography>
            </Box>
        </TableCell>

        {/* Min Order */}
        <TableCell>
            {coupon.MinOrderAmount > 0
                ? `₹${parseFloat(coupon.MinOrderAmount).toLocaleString()}`
                : <Typography sx={{ color: 'rgba(255,255,255,0.3)' }}>—</Typography>}
        </TableCell>

        {/* Max Discount */}
        <TableCell>
            {coupon.MaxDiscountAmount
                ? `₹${parseFloat(coupon.MaxDiscountAmount).toLocaleString()}`
                : <Typography sx={{ color: 'rgba(255,255,255,0.3)' }}>—</Typography>}
        </TableCell>

        {/* Expiry */}
        <TableCell>
            {coupon.ExpiryDate
                ? <Chip
                    label={new Date(coupon.ExpiryDate).toLocaleDateString('en-GB')}
                    size="small"
                    sx={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        borderRadius: 1.5,
                        background: expired ? 'rgba(244,67,54,0.15)' : 'rgba(188,150,94,0.15)',
                        border: `1px solid ${expired ? 'rgba(244,67,54,0.4)' : 'rgba(188,150,94,0.4)'}`,
                        color: expired ? '#f44336' : '#d4b483',
                    }}
                />
                : <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>No expiry</Typography>
            }
        </TableCell>

        {/* Applies To */}
        <TableCell>
            {coupon.ApplicableTo === 'product'
                ? <Chip
                    label={coupon.ProductCode}
                    size="small"
                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem', borderRadius: 1.5, backgroundColor: 'rgba(33,150,243,0.15)', color: '#2196f3' }}
                />
                : <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>All Products</Typography>
            }
        </TableCell>

        {/* Status */}
        <TableCell>
            <Chip
                label={expired ? 'Expired' : coupon.IsActive ? 'Active' : 'Inactive'}
                size="small"
                onClick={() => !expired && onToggleActive(coupon)}
                sx={{
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    borderRadius: 5,
                    cursor: expired ? 'default' : 'pointer',
                    background: expired
                        ? 'rgba(244,67,54,0.15)'
                        : coupon.IsActive ? 'rgba(76,175,80,0.15)' : 'rgba(158,158,158,0.15)',
                    border: `1px solid ${expired
                        ? 'rgba(244,67,54,0.4)'
                        : coupon.IsActive ? 'rgba(76,175,80,0.4)' : 'rgba(158,158,158,0.4)'}`,
                    color: expired ? '#f44336' : coupon.IsActive ? '#4caf50' : '#9e9e9e',
                }}
            />
        </TableCell>

        {/* Actions */}
        <TableCell align="right">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
                <SquareActionButton
                    icon={<EditIcon sx={{ fontSize: 16 }} />}
                    color="#bc965e"
                    bg="rgba(188,150,94,0.15)"
                    border="rgba(188,150,94,0.5)"
                    onClick={() => onEdit(coupon)}
                />
                <SquareActionButton
                    icon={<DeleteIcon sx={{ fontSize: 16 }} />}
                    color="#f44336"
                    bg="rgba(244,67,54,0.15)"
                    border="rgba(244,67,54,0.5)"
                    onClick={() => onDelete(coupon.CouponId)}
                />
            </Box>
        </TableCell>
    </TableRow>
));

const AdminCoupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);

    const [addOpen, setAddOpen] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => { loadCoupons(); }, []);

    const loadCoupons = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/coupons');
            if (res.data.success) setCoupons(res.data.data);
            else toast.error('Failed to load coupons');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load coupons');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        try {
            const res = await api.delete(`/coupons/${deletingId}`);
            if (res.data.success) { toast.success('Coupon deleted'); loadCoupons(); }
            else toast.error(res.data.message || 'Failed to delete');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete coupon');
        } finally {
            setDeleteDialogOpen(false);
            setDeletingId(null);
        }
    }, [deletingId, loadCoupons]);

    const handleToggleActive = useCallback(async (coupon) => {
        try {
            const res = await api.put(`/coupons/${coupon.CouponId}`, {
                ...coupon,
                IsActive: !coupon.IsActive
            });
            if (res.data.success) {
                loadCoupons();
                toast.success(`Coupon ${!coupon.IsActive ? 'activated' : 'deactivated'}`);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update coupon');
        }
    }, [loadCoupons]);

    const handleCopyCode = useCallback((code) => {
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(code).then(
                () => toast.success(`Copied: ${code}`),
                () => toast.error('Copy failed')
            );
        } else {
            toast.error('Clipboard not available');
        }
    }, []);

    const handleEdit = useCallback((coupon) => setEditingCoupon(coupon), []);
    const handleDeleteClick = useCallback((id) => {
        setDeletingId(id);
        setDeleteDialogOpen(true);
    }, []);

    // Single pass: compute expired flag once per coupon, derive all stats
    // together instead of running coupons.filter(...) four separate times.
    const { rows, stats } = useMemo(() => {
        const now = Date.now();
        let active = 0, inactive = 0, expiredCount = 0;

        const rows = coupons.map((c) => {
            const expired = !!c.ExpiryDate && new Date(c.ExpiryDate).getTime() < now;
            if (expired) expiredCount++;
            else if (c.IsActive) active++;
            if (!c.IsActive) inactive++;
            return { coupon: c, expired };
        });

        return {
            rows,
            stats: { total: coupons.length, active, inactive, expired: expiredCount },
        };
    }, [coupons]);

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                    <CircularProgress size={40} sx={{ color: '#bc965e' }} />
                    <Typography className="glass-text-secondary">Loading coupons...</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4, px: { xs: 1.5, sm: 2, md: 3 } }}>

            {/* ── Header ── */}
            <Box sx={{ mb: 4 }} className="glass-content-container">
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CouponIcon sx={{ color: '#bc965e', fontSize: 32 }} />
                        <Box>
                            <Typography variant="h4" className="glass-title">Coupons</Typography>
                            <Typography variant="body2" className="glass-text-secondary">
                                Manage discount coupons for your customers
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setAddOpen(true)}
                        fullWidth={isMobile}
                        sx={{
                            background: 'linear-gradient(45deg, #bc965e, #d4af37)',
                            fontWeight: 600,
                            borderRadius: 2,
                            '&:hover': { background: 'linear-gradient(45deg, #a8834d, #c19b2a)' },
                        }}
                    >
                        New Coupon
                    </Button>
                </Box>

                {/* Stats row */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                    gap: 2,
                    mt: 3,
                }}>
                    {STAT_CARDS.map(stat => (
                        <Paper key={stat.key} elevation={0} sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            p: { xs: 2, sm: 2.5 },
                            textAlign: 'center',
                            background: '#0f0d0a',
                            border: `1px solid rgba(188, 150, 94, 0.64)`,
                            borderRadius: 2,
                        }}>
                            <Box sx={{
                                position: 'absolute', top: '50%', left: '50%',
                                width: 90, height: 90,
                                transform: 'translate(-50%, -50%)',
                                background: `radial-gradient(circle, ${stat.color}22 0%, transparent 70%)`,
                                pointerEvents: 'none',
                            }} />
                            <Typography variant="h4" sx={{ color: stat.color, fontWeight: 700, position: 'relative' }}>
                                {stats[stat.key]}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', position: 'relative' }}>
                                {stat.label}
                            </Typography>
                        </Paper>
                    ))}
                </Box>
            </Box>

            {/* ── Coupons Table ── */}
            <Paper elevation={0} className="glass-content-container" sx={{ p: 0, overflow: 'hidden', position: 'relative' }}>
                {coupons.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <CouponIcon sx={{ fontSize: 64, color: 'rgba(188,150,94,0.3)', mb: 2 }} />
                        <Typography variant="h6" className="glass-text-primary">No coupons yet</Typography>
                        <Typography variant="body2" className="glass-text-secondary" sx={{ mb: 3 }}>
                            Create your first coupon to start offering discounts
                        </Typography>
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}
                            sx={{ borderColor: 'rgba(188,150,94,0.5)', color: '#bc965e' }}>
                            Create Coupon
                        </Button>
                    </Box>
                ) : (
                    <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <Table sx={{ minWidth: 750 }}>
                            <TableHead>
                                <TableRow sx={{
                                    '& .MuiTableCell-root': {
                                        borderColor: 'rgba(188,150,94,0.15)',
                                        color: 'rgba(255,255,255,0.5)',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: 1,
                                        whiteSpace: 'nowrap',
                                    }
                                }}>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Discount</TableCell>
                                    <TableCell>Min Order</TableCell>
                                    <TableCell>Max Discount</TableCell>
                                    <TableCell>Expiry</TableCell>
                                    <TableCell>Applies To</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(({ coupon, expired }) => (
                                    <CouponRow
                                        key={coupon.CouponId}
                                        coupon={coupon}
                                        expired={expired}
                                        onCopy={handleCopyCode}
                                        onToggleActive={handleToggleActive}
                                        onEdit={handleEdit}
                                        onDelete={handleDeleteClick}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* ── Add Coupon ── */}
            <AddCouponDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onCreated={loadCoupons}
            />

            {/* ── Edit Coupon ── */}
            <EditCouponDialog
                open={!!editingCoupon}
                coupon={editingCoupon}
                onClose={() => setEditingCoupon(null)}
                onUpdated={loadCoupons}
            />

            {/* ── Delete Confirmation ── */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle>Delete Coupon?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        This action cannot be undone. The coupon will no longer be usable by customers.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
                    <Button fullWidth={isMobile} onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button fullWidth={isMobile} variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>

        </Container>
    );
};

export default AdminCoupons;